'use client';

import { c as _c } from "react/compiler-runtime";
import { getImageProxy, isSdkError, useSearchImages } from '@morfi/backend-sdk-v2/client';
import type { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { ImageShimmer } from '@/components/images/optimized-image';
import { Alert, AlertDescription } from '@/components/ui/app-alert';
import { Icon } from '@/components/ui/icon';
import { Search01Icon } from '@/components/ui/icon/glyphs';
import { InfiniteList } from '@/components/ui/infinite-list';
import { onEvent } from '@/events';
import type { ImageGenerationMode, ImageSearchFailureReason } from '@/events/image-generation';
import { computeJustifiedLayout } from '@/lib/ui/justified-layout';
const JUSTIFIED_TARGET_ROW_HEIGHT_PX = 140;
const JUSTIFIED_GAP_PX = 8;
export type ImageSearchProvider = 'serper';
export interface ImageSearchResult {
  thumbnailUrl: string;
  fullUrl: string;
  width: number;
  height: number;
  sourceDomain: string;
  sourceUrl: string;
}
export interface ImageSearchPage {
  results: ImageSearchResult[];
  provider: ImageSearchProvider;
  nextCursor?: string;
}
export interface SearchGridProps {
  query: string;
  mode: ImageGenerationMode;
  startTimeRef: {
    current: number;
  };
  lastSeenPageCountRef: {
    current: number;
  };
  onSelect: (result: ImageSearchResult) => void;
  onError: (reason: ImageSearchFailureReason) => void;
  onSuccess: () => void;
  /** Result id currently fetching its full-size blob; that tile renders a spinner overlay and other tiles are inert until it resolves. */
  loadingResultUrl?: string | null | undefined;
  /** Fired when the user hovers a tile — parent should warm the full-size proxy fetch so the eventual click feels instant. */
  onPrefetch?: ((result: ImageSearchResult) => void) | undefined;
  t: ReturnType<typeof useTranslations>;
}
export function SearchGrid(t0) {
  const $ = _c(67);
  const {
    query,
    mode,
    startTimeRef,
    lastSeenPageCountRef,
    onSelect,
    onError,
    onSuccess,
    loadingResultUrl: t1,
    onPrefetch,
    t
  } = t0;
  const loadingResultUrl = t1 === undefined ? null : t1;
  let t2;
  if ($[0] !== query) {
    t2 = query.trim();
    $[0] = query;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const trimmed = t2;
  const enabled = trimmed.length > 0;
  const searchMutation = useSearchImages();
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [pages, setPages] = useState(t3);
  const [searchError, setSearchError] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const requestVersionRef = useRef(0);
  let t4;
  if ($[3] !== enabled || $[4] !== searchMutation || $[5] !== trimmed) {
    t4 = () => {
      requestVersionRef.current = requestVersionRef.current + 1;
      const version = requestVersionRef.current;
      setPages([]);
      setSearchError(null);
      setIsFetchingNextPage(false);
      if (!enabled) {
        setIsInitialLoading(false);
        return;
      }
      setIsInitialLoading(true);
      searchMutation.mutateAsync({
        query: trimmed
      }).then(page => {
        if (version !== requestVersionRef.current) {
          return;
        }
        setPages([page as ImageSearchPage]);
        setIsInitialLoading(false);
      }).catch(err => {
        if (version !== requestVersionRef.current) {
          return;
        }
        setSearchError(err);
        setIsInitialLoading(false);
      });
    };
    $[3] = enabled;
    $[4] = searchMutation;
    $[5] = trimmed;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== enabled || $[8] !== searchMutation.mutateAsync || $[9] !== trimmed) {
    t5 = [trimmed, enabled, searchMutation.mutateAsync];
    $[7] = enabled;
    $[8] = searchMutation.mutateAsync;
    $[9] = trimmed;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useEffect(t4, t5);
  const hasNextPage = pages.length > 0 && pages[pages.length - 1] !== undefined && (pages[pages.length - 1] as ImageSearchPage).nextCursor !== undefined;
  let t6;
  if ($[11] !== hasNextPage || $[12] !== isFetchingNextPage || $[13] !== pages || $[14] !== searchMutation || $[15] !== trimmed) {
    t6 = function fetchNextPage() {
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }
      const last = pages[pages.length - 1];
      if (last?.nextCursor === undefined) {
        return;
      }
      requestVersionRef.current = requestVersionRef.current + 1;
      const version_0 = requestVersionRef.current;
      setIsFetchingNextPage(true);
      searchMutation.mutateAsync({
        query: trimmed,
        cursor: last.nextCursor
      }).then(page_0 => {
        if (version_0 !== requestVersionRef.current) {
          return;
        }
        setPages(prev => [...prev, page_0 as ImageSearchPage]);
        setIsFetchingNextPage(false);
      }).catch(err_0 => {
        if (version_0 !== requestVersionRef.current) {
          return;
        }
        setSearchError(err_0);
        setIsFetchingNextPage(false);
      });
    };
    $[11] = hasNextPage;
    $[12] = isFetchingNextPage;
    $[13] = pages;
    $[14] = searchMutation;
    $[15] = trimmed;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const fetchNextPage = t6;
  const isError = searchError !== null;
  let t7;
  if ($[17] !== isError || $[18] !== lastSeenPageCountRef || $[19] !== mode || $[20] !== onError || $[21] !== onSuccess || $[22] !== pages || $[23] !== searchError || $[24] !== startTimeRef) {
    t7 = () => {
      if (isError) {
        const reason = classifySearchErrorOuter(searchError);
        onError(reason);
        onEvent("image_search_failed", {
          mode,
          reason
        });
        return;
      }
      const pageCount = pages.length;
      if (pageCount > lastSeenPageCountRef.current && pageCount > 0) {
        const latest = pages[pageCount - 1];
        if (latest !== undefined) {
          lastSeenPageCountRef.current = pageCount;
          onEvent("image_search_completed", {
            mode,
            provider: latest.provider,
            resultCount: latest.results.length,
            latencyMs: Math.max(0, Date.now() - startTimeRef.current)
          });
          onSuccess();
        }
      }
    };
    $[17] = isError;
    $[18] = lastSeenPageCountRef;
    $[19] = mode;
    $[20] = onError;
    $[21] = onSuccess;
    $[22] = pages;
    $[23] = searchError;
    $[24] = startTimeRef;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== isError || $[27] !== mode || $[28] !== pages || $[29] !== searchError) {
    t8 = [pages, isError, searchError, mode];
    $[26] = isError;
    $[27] = mode;
    $[28] = pages;
    $[29] = searchError;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  useEffect(t7, t8);
  if (!enabled) {
    let t9;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="flex size-12 items-center justify-center rounded-full bg-background-tertiary"><Icon icon={Search01Icon} className="size-6 text-foreground-tertiary" aria-hidden="true" /></div>;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    let t10;
    if ($[32] !== t) {
      t10 = t("search.state.idle_title");
      $[32] = t;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    let t11;
    if ($[34] !== t10) {
      t11 = <p className="text-body text-foreground-primary">{t10}</p>;
      $[34] = t10;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    let t12;
    if ($[36] !== t) {
      t12 = t("search.state.idle_hint");
      $[36] = t;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    let t13;
    if ($[38] !== t12) {
      t13 = <p className="text-caption text-foreground-tertiary">{t12}</p>;
      $[38] = t12;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    let t14;
    if ($[40] !== t11 || $[41] !== t13) {
      t14 = <div data-testid="image-search-grid" className="flex min-h-48 flex-col items-center justify-center gap-2 px-6 py-10 text-center">{t9}{t11}{t13}</div>;
      $[40] = t11;
      $[41] = t13;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    return t14;
  }
  if (isError) {
    const reason_0 = classifySearchErrorOuter(searchError);
    const key = reason_0 === "rate_limit" ? "rateLimit" : reason_0 === "network" ? "network" : "failed";
    const t9 = `search.error.${key}`;
    let t10;
    if ($[43] !== t || $[44] !== t9) {
      t10 = t(t9);
      $[43] = t;
      $[44] = t9;
      $[45] = t10;
    } else {
      t10 = $[45];
    }
    let t11;
    if ($[46] !== t10) {
      t11 = <Alert variant="destructive" data-testid="image-search-error"><AlertDescription>{t10}</AlertDescription></Alert>;
      $[46] = t10;
      $[47] = t11;
    } else {
      t11 = $[47];
    }
    return t11;
  }
  let t9;
  if ($[48] !== pages) {
    t9 = pages.flatMap(_temp);
    $[48] = pages;
    $[49] = t9;
  } else {
    t9 = $[49];
  }
  const allResults = t9;
  if (!isInitialLoading && allResults.length === 0) {
    let t10;
    if ($[50] !== t) {
      t10 = t("search.state.empty");
      $[50] = t;
      $[51] = t10;
    } else {
      t10 = $[51];
    }
    let t11;
    if ($[52] !== t10) {
      t11 = <p className="py-6 text-center text-body text-foreground-secondary" data-testid="image-search-empty">{t10}</p>;
      $[52] = t10;
      $[53] = t11;
    } else {
      t11 = $[53];
    }
    return t11;
  }
  let t10;
  if ($[54] !== allResults || $[55] !== isFetchingNextPage || $[56] !== isInitialLoading || $[57] !== loadingResultUrl || $[58] !== onPrefetch || $[59] !== onSelect || $[60] !== t) {
    t10 = isInitialLoading ? <JustifiedGridSkeleton /> : <><JustifiedSearchResults results={allResults} onSelect={onSelect} loadingResultUrl={loadingResultUrl} onPrefetch={onPrefetch} attribution={r => t("search.attribution", {
        domain: r.sourceDomain
      })} />{isFetchingNextPage && <JustifiedGridSkeleton rows={1} />}</>;
    $[54] = allResults;
    $[55] = isFetchingNextPage;
    $[56] = isInitialLoading;
    $[57] = loadingResultUrl;
    $[58] = onPrefetch;
    $[59] = onSelect;
    $[60] = t;
    $[61] = t10;
  } else {
    t10 = $[61];
  }
  let t11;
  if ($[62] !== fetchNextPage || $[63] !== hasNextPage || $[64] !== isFetchingNextPage || $[65] !== t10) {
    t11 = <InfiniteList hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} maxHeight={384} className="p-1" data-testid="image-search-grid">{t10}</InfiniteList>;
    $[62] = fetchNextPage;
    $[63] = hasNextPage;
    $[64] = isFetchingNextPage;
    $[65] = t10;
    $[66] = t11;
  } else {
    t11 = $[66];
  }
  return t11;
}
function _temp(p) {
  return p.results;
}
interface JustifiedSearchResultsProps {
  results: ReadonlyArray<ImageSearchResult>;
  onSelect: (result: ImageSearchResult) => void;
  loadingResultUrl?: string | null | undefined;
  onPrefetch?: ((result: ImageSearchResult) => void) | undefined;
  attribution: (result: ImageSearchResult) => string;
}

/**
 * Google Images-style justified rows: every image keeps its native aspect
 * ratio, rows fill the container width at a target row height, and the
 * trailing partial row is left-aligned at target height instead of being
 * stretched.
 */
function JustifiedSearchResults(t0) {
  const $ = _c(21);
  const {
    results,
    onSelect,
    loadingResultUrl: t1,
    onPrefetch,
    attribution
  } = t0;
  const loadingResultUrl = t1 === undefined ? null : t1;
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const node = containerRef.current;
      if (node === null) {
        return;
      }
      setContainerWidth(node.clientWidth);
      const ro = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry === undefined) {
          return;
        }
        const next = Math.floor(entry.contentRect.width);
        setContainerWidth(prev => prev === next ? prev : next);
      });
      ro.observe(node);
      return () => {
        ro.disconnect();
      };
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  let t6;
  if ($[2] !== attribution || $[3] !== containerWidth || $[4] !== loadingResultUrl || $[5] !== onPrefetch || $[6] !== onSelect || $[7] !== results) {
    const rows = computeJustifiedLayout(results.map(_temp2), {
      containerWidth,
      targetRowHeight: JUSTIFIED_TARGET_ROW_HEIGHT_PX,
      gap: JUSTIFIED_GAP_PX
    });
    t4 = containerRef;
    t5 = "flex flex-col gap-2";
    let t7;
    if ($[11] !== attribution || $[12] !== loadingResultUrl || $[13] !== onPrefetch || $[14] !== onSelect || $[15] !== results) {
      t7 = (row, rowIdx) => {
        const baseIdx = row.startIndex;
        const slice = results.slice(baseIdx, baseIdx + row.cells.length);
        return <div key={`row-${rowIdx}`} className="flex flex-row gap-2">{slice.map((r_0, i) => {
            const cell = row.cells[i];
            if (cell === undefined) {
              return null;
            }
            const absoluteIdx = baseIdx + i;
            const isAboveFold = absoluteIdx < 8;
            const isThisLoading = loadingResultUrl === r_0.fullUrl;
            const isAnyLoading = loadingResultUrl !== null;
            return <button key={`${r_0.fullUrl}-${String(absoluteIdx)}`} type="button" disabled={isAnyLoading && !isThisLoading} aria-busy={isThisLoading} onClick={() => {
              onSelect(r_0);
            }} onPointerEnter={() => {
              onPrefetch?.(r_0);
            }} onFocus={() => {
              onPrefetch?.(r_0);
            }} style={{
              width: `${cell.width}px`,
              height: `${cell.height}px`
            }} className="group relative shrink-0 cursor-pointer overflow-hidden rounded-control bg-background-tertiary transition-transform duration-fast ease-default hover:scale-[1.015] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100 disabled:active:scale-100 motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100" aria-label={attribution(r_0)} data-testid="image-search-thumb"><img src={getImageProxy({
                url: r_0.thumbnailUrl
              })} alt={attribution(r_0)} loading={isAboveFold ? "eager" : "lazy"} fetchPriority={isAboveFold ? "high" : "auto"} decoding="async" referrerPolicy="no-referrer" onError={e => {
                const el = e.currentTarget;
                const fallback = getImageProxy({
                  url: r_0.fullUrl
                });
                if (el.src !== fallback) {
                  el.src = fallback;
                }
              }} className="size-full object-cover" />{isThisLoading && <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-surface-scrim-default" aria-hidden="true"><div className="size-6 animate-spin rounded-full border-2 border-border-brand border-t-transparent motion-reduce:animate-none" /></div>}</button>;
          })}</div>;
      };
      $[11] = attribution;
      $[12] = loadingResultUrl;
      $[13] = onPrefetch;
      $[14] = onSelect;
      $[15] = results;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t6 = rows.map(t7);
    $[2] = attribution;
    $[3] = containerWidth;
    $[4] = loadingResultUrl;
    $[5] = onPrefetch;
    $[6] = onSelect;
    $[7] = results;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t7 = <div ref={t4} className={t5}>{t6}</div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
function _temp2(r) {
  return {
    width: r.width,
    height: r.height
  };
}
interface JustifiedGridSkeletonProps {
  rows?: number;
}

// Pre-baked aspect ratios used to seed the loading skeleton. The pattern
// is run through the same justified-layout solver as the live grid, so the
// skeleton tiles fill the container width identically to real results and
// stay responsive to viewport changes.
const SKELETON_ASPECTS: ReadonlyArray<{
  width: number;
  height: number;
}> = [{
  width: 16,
  height: 9
},
// landscape 16:9
{
  width: 4,
  height: 3
},
// landscape 4:3
{
  width: 3,
  height: 4
},
// portrait 3:4
{
  width: 1,
  height: 1
},
// square
{
  width: 3,
  height: 2
},
// landscape 3:2
{
  width: 2,
  height: 3
},
// portrait 2:3
{
  width: 4,
  height: 3
},
// landscape 4:3
{
  width: 16,
  height: 9
},
// landscape 16:9
{
  width: 1,
  height: 1
},
// square
{
  width: 3,
  height: 2
},
// landscape 3:2
{
  width: 4,
  height: 5
},
// portrait 4:5
{
  width: 5,
  height: 4
} // landscape 5:4
];

/**
 * Loading shimmer that mimics the justified-rows shape: each row is a single
 * flex line of mixed-aspect tiles at the target row height, so the loading
 * state visually rhymes with the loaded layout.
 */
function JustifiedGridSkeleton(t0) {
  const $ = _c(11);
  const {
    rows: t1
  } = t0;
  const rows = t1 === undefined ? 3 : t1;
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const node = containerRef.current;
      if (node === null) {
        return;
      }
      setContainerWidth(node.clientWidth);
      const ro = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry === undefined) {
          return;
        }
        const next = Math.floor(entry.contentRect.width);
        setContainerWidth(prev => prev === next ? prev : next);
      });
      ro.observe(node);
      return () => {
        ro.disconnect();
      };
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  let t6;
  if ($[2] !== containerWidth || $[3] !== rows) {
    const layoutRows = computeJustifiedLayout(SKELETON_ASPECTS, {
      containerWidth,
      targetRowHeight: JUSTIFIED_TARGET_ROW_HEIGHT_PX,
      gap: JUSTIFIED_GAP_PX
    }).slice(0, rows);
    t4 = containerRef;
    t5 = "flex flex-col gap-2";
    t6 = layoutRows.map(_temp3);
    $[2] = containerWidth;
    $[3] = rows;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = <div ref={t4} className={t5}>{t6}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}

// ALLOW-UNKNOWN: error classifier accepts arbitrary thrown values; narrows via the v2 SdkError type guard.
function _temp3(row, rowIdx) {
  return <div key={`shimmer-row-${rowIdx}`} className="flex flex-row gap-2">{row.cells.map((cell, i) => <div key={`shimmer-cell-${rowIdx}-${i}`} style={{
      width: `${cell.width}px`,
      height: `${cell.height}px`
    }} className="relative shrink-0 overflow-hidden rounded-control bg-background-tertiary"><ImageShimmer /></div>)}</div>;
}
function classifySearchErrorOuter(err: unknown): ImageSearchFailureReason {
  if (isSdkError(err)) {
    if (err.category === 'rate-limited') return 'rate_limit';
    if (err.category === 'network') return 'network';
    return 'unknown';
  }
  if (err instanceof TypeError) return 'network';
  return 'unknown';
}